import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { CaretRight, FileXls } from "@phosphor-icons/react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const COLS = [["g1", "G1 Total sales (incl. GST)"], ["g2", "G2 Export sales"], ["a1", "1A GST on sales"], ["b1", "1B GST on purchases"], ["net", "Net GST payable"]];

function exportBas(data) {
  const wb = XLSX.utils.book_new();
  const head = ["Quarter", "Period", ...COLS.map(([, l]) => l)];
  const rows = data.quarters.map((q) => [`${q.quarter} FY${data.fy}`, `${q.start} – ${q.end}`, ...COLS.map(([k]) => q[k])]);
  rows.push(["Total", "", ...COLS.map(([k]) => data.totals[k])]);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...rows]), "BAS summary");
  const docs = [["Quarter", "Document", "Type", "Customer", "Date", "Total (incl. GST)", "GST", "Export"]];
  data.quarters.forEach((q) => q.docs.forEach((d) => docs.push([q.quarter, d.doc_no, d.doc_type, d.customer_name, d.date, d.total, d.gst, d.export ? "Yes" : ""])));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(docs), "Documents");
  XLSX.writeFile(wb, `BAS-FY${data.fy}.xlsx`);
}

const QuarterDocs = ({ q }) => {
  const navigate = useNavigate();
  if (!q.docs.length) return <tr><td colSpan={7} className="px-4 py-3 text-sm text-muted-foreground bg-muted/30">No posted invoices or credit memos in this quarter.</td></tr>;
  return q.docs.map((d) => (
    <tr key={d.id} className="bg-muted/30 text-sm hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/sales/doc/${d.id}`)} data-testid={`bas-doc-${d.doc_no}`}>
      <td className="px-4 py-1.5 pl-10 font-mono text-primary" colSpan={2}>{d.doc_no} <span className="text-muted-foreground font-sans">· {d.customer_name} · {d.date}{d.export ? " · export" : ""}</span></td>
      <td className="px-4 py-1.5 text-right tabular">{money(d.total)}</td>
      <td className="px-4 py-1.5 text-right tabular">{d.export ? money(d.total) : "—"}</td>
      <td className="px-4 py-1.5 text-right tabular">{money(d.gst)}</td>
      <td colSpan={2} />
    </tr>
  ));
};

export const BasReport = () => {
  const [fy, setFy] = useState(0);
  const [open, setOpen] = useState(null);
  const { data } = useQuery({ queryKey: ["bas", fy], queryFn: async () => (await api.get("/reports/bas", { params: fy ? { fy } : {} })).data });
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="bas-report">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold mr-auto">Business Activity Statement — GST</h2>
        <Select value={String(data.fy)} onValueChange={(v) => { setFy(Number(v)); setOpen(null); }}>
          <SelectTrigger className="w-60 rounded-sm" data-testid="bas-fy-select" data-no-translate><SelectValue /></SelectTrigger>
          <SelectContent data-no-translate>{data.years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`bas-fy-${y}`}>FY{y} (Jul {y - 1} – Jun {y})</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" className="rounded-sm h-9" onClick={() => exportBas(data)} data-testid="bas-export"><FileXls size={16} className="mr-1.5" /> Excel</Button>
      </div>
      <p className="text-xs text-muted-foreground">Accrual basis: posted sales invoices less credit memos, by posting date, in AUD. Purchases carry no GST, so 1B is 0.</p>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-4 py-2 text-left">Quarter</th><th className="px-4 py-2 text-left">Period</th>{COLS.map(([k, l]) => <th key={k} className="px-4 py-2 text-right whitespace-nowrap">{l}</th>)}</tr>
          </thead>
          <tbody>
            {data.quarters.map((q) => (
              <Fragment key={q.quarter}>
                <tr className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => setOpen(open === q.quarter ? null : q.quarter)} data-testid={`bas-row-${q.quarter}`}>
                  <td className="px-4 py-2.5 font-semibold"><CaretRight size={12} className={`inline mr-1 transition-transform ${open === q.quarter ? "rotate-90" : ""}`} />{q.quarter} <span className="font-normal text-muted-foreground">{q.label}</span></td>
                  <td className="px-4 py-2.5 text-muted-foreground tabular whitespace-nowrap">{q.start} – {q.end}</td>
                  {COLS.map(([k]) => <td key={k} className={`px-4 py-2.5 text-right tabular ${k === "net" ? "font-bold text-primary" : ""}`} data-testid={`bas-${q.quarter}-${k}`}>{money(q[k])}</td>)}
                </tr>
                {open === q.quarter && <QuarterDocs q={q} />}
              </Fragment>
            ))}
            <tr className="border-t-2 border-border font-bold">
              <td className="px-4 py-2.5" colSpan={2}>Total <span data-no-translate>FY{data.fy}</span></td>
              {COLS.map(([k]) => <td key={k} className="px-4 py-2.5 text-right tabular" data-testid={`bas-total-${k}`}>{money(data.totals[k])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
